import { Camera, Check } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { parseError } from '@/lib/errors'
import { useDeletePhoto, type User, useUploadPhoto } from '@/models/user'

// Mirrors UserPhotoSerializer.validate_photo. Checked client-side too so an
// oversized file is refused with a reason rather than round-tripping to a 400.
const ACCEPTED = ['.png', '.jpg', '.jpeg', '.webp']
const MAX_MB = 5

/**
 * Upload the headshot that stands in for you everywhere — internally on your
 * activity, assignments and the staff list, and to customers on the broker card
 * of the "coverage is active" email. Customer-facing surfaces show no image at
 * all when none is set; internal ones fall back to your legacy avatar, then to
 * your initials.
 *
 * The avatar itself is the file picker: clicking it stages a photo and previews
 * it in place, so you see the crop before committing with Save.
 */
export function BrokerPhotoCard({ user }: { user: User }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [staged, setStaged] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const upload = useUploadPhoto()
  const remove = useDeletePhoto()

  // Object URLs leak until revoked, and one is created per pick.
  useEffect(() => {
    if (!staged) {
      setPreview(null)
      return
    }
    const url = URL.createObjectURL(staged)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [staged])

  function handlePick(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    // Reset first: without it, re-picking the same file fires no change event.
    event.target.value = ''
    if (!file) return

    if (!ACCEPTED.some((ext) => file.name.toLowerCase().endsWith(ext))) {
      toast.error(`Unsupported image type. Use one of: ${ACCEPTED.join(', ')}.`)
      return
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Image must be ${MAX_MB} MB or smaller.`)
      return
    }
    setStaged(file)
  }

  function handleSave() {
    if (!staged) return
    upload.mutate(staged, {
      onSuccess: () => {
        toast.success('Photo updated')
        setStaged(null)
      },
      onError: (err) => toast.error(parseError(err)),
    })
  }

  function handleRemove() {
    remove.mutate(undefined, {
      onSuccess: () => toast.success('Photo removed'),
      onError: (err) => toast.error(parseError(err)),
    })
  }

  const shown = preview ?? user.photo

  return (
    <Card>
      <CardHeader>
        <CardTitle>Your photo</CardTitle>
        <CardDescription>
          Used wherever you appear — your activity, lead assignments and the staff list — and shown to
          customers when you're their assigned broker, including in the coverage confirmation email. Emails
          omit the photo entirely if you don't set one.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={upload.isPending}
              aria-label={user.photo ? 'Change your photo' : 'Add a photo'}
              title={user.photo ? 'Change your photo' : 'Add a photo'}
              className="group focus-visible:ring-ring/50 relative size-20 shrink-0 cursor-pointer overflow-hidden rounded-full border outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {shown ? (
                <img src={shown} alt="" className="size-full object-cover" />
              ) : (
                <span className="text-muted-foreground flex size-full items-center justify-center text-xs">
                  No photo
                </span>
              )}
              {/* Nothing about a round image says "clickable", so reveal the
                  affordance on hover and keyboard focus. */}
              <span className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                <Camera className="size-6 text-white" />
              </span>
            </button>

            <input
              ref={inputRef}
              type="file"
              accept={ACCEPTED.join(',')}
              onChange={handlePick}
              className="hidden"
              aria-hidden="true"
            />

            {staged && (
              <Button size="sm" onClick={handleSave} loading={upload.isPending ? 'Saving…' : undefined}>
                <Check />
                Save
              </Button>
            )}

            {user.photo && !staged && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRemove}
                loading={remove.isPending ? 'Removing…' : undefined}
              >
                Remove
              </Button>
            )}
          </div>

          <p className="text-muted-foreground text-xs">
            {staged
              ? `${staged.name} — save to apply`
              : `Click the photo to choose a new one. PNG, JPG or WEBP, max ${MAX_MB} MB.`}
          </p>
        </div>
      </CardContent>
    </Card>
  )
}
